import React, { useState } from 'react';
import Card from '../components/ProfileCard.tsx';
import Button from '../components/Button';
import Badge from '../components/Badge';
import SearchInput from '../components/SearchInput';
import { Edit, Trash2, Plus } from 'lucide-react';

const AvailableServices: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingService, setEditingService] = useState<Service | null>(null);

    interface Service {
        id: number;
        name: string;
        price: number;
        description: string;
        type: 'Installation' | 'Customization' | 'Support' | 'Maintenance';
        isPopular: boolean;
    }

    // Sample data
    const [services, setServices] = useState<Service[]>([
        {
            id: 1,
            name: 'Basic Installation',
            price: 49.99,
            description: 'We\'ll install your template on your server and set up the basic configuration.',
            type: 'Installation',
            isPopular: true,
        },
        {
            id: 2,
            name: 'Premium Installation',
            price: 99.99,
            description: 'We\'ll install your template, configure it, and import demo content to match the preview.',
            type: 'Installation',
            isPopular: false,
        },
        {
            id: 3,
            name: 'Color Scheme Customization',
            price: 79.99,
            description: 'We\'ll customize the color scheme of your template to match your brand colors.',
            type: 'Customization',
            isPopular: true,
        },
        {
            id: 4,
            name: 'Layout Customization',
            price: 149.99,
            description: 'We\'ll customize the layout of your template to match your specific requirements.',
            type: 'Customization',
            isPopular: false,
        },
        {
            id: 5,
            name: 'Basic Support (1 month)',
            price: 29.99,
            description: 'Email support for one month to help you with any issues or questions.',
            type: 'Support',
            isPopular: true,
        },
        {
            id: 6,
            name: 'Premium Support (3 months)',
            price: 69.99,
            description: 'Email and chat support for three months, with priority response times.',
            type: 'Support',
            isPopular: false,
        },
        {
            id: 7,
            name: 'Monthly Maintenance',
            price: 39.99,
            description: 'We\'ll keep your template updated and perform regular backups.',
            type: 'Maintenance',
            isPopular: true,
        },
        {
            id: 8,
            name: 'Yearly Maintenance',
            price: 399.99,
            description: 'We\'ll keep your template updated, perform regular backups, and provide quarterly performance reports.',
            type: 'Maintenance',
            isPopular: false,
        },
    ]);

    // Filter and search
    const filteredServices = services.filter((service) => {
        return (
            service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            service.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            service.type.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    // Group services by type
    const groupedServices = filteredServices.reduce((acc, service) => {
        if (!acc[service.type]) {
            acc[service.type] = [];
        }
        acc[service.type].push(service);
        return acc;
    }, {} as Record<string, Service[]>);

    const [newService, setNewService] = useState<Omit<Service, 'id'>>({
        name: '',
        price: 0,
        description: '',
        type: 'Installation',
        isPopular: false,
    });

    const handleAddService = () => {
        const id = Math.max(0, ...services.map(s => s.id)) + 1;
        setServices([...services, { ...newService, id }]);
        setNewService({
            name: '',
            price: 0,
            description: '',
            type: 'Installation',
            isPopular: false,
        });
        setShowAddModal(false);
    };

    const handleUpdateService = () => {
        if (editingService) {
            setServices(services.map(s => s.id === editingService.id ? editingService : s));
            setEditingService(null);
        }
    };

    const handleDeleteService = (id: number) => {
        setServices(services.filter(s => s.id !== id));
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Available Services</h1>
                <p className="mt-1 text-sm text-gray-500">
                    Manage the services you offer to customers
                </p>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <SearchInput
                    placeholder="Search services..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="md:w-64"
                />

                <Button
                    variant="primary"
                    className="flex items-center"
                    onClick={() => setShowAddModal(true)}
                >
                    <Plus size={16} className="mr-2" />
                    Add New Service
                </Button>
            </div>

            <div className="space-y-8">
                {Object.keys(groupedServices).length > 0 ? (
                    Object.entries(groupedServices).map(([type, services]) => (
                        <div key={type} className="space-y-4">
                            <h2 className="text-xl font-semibold text-gray-900">{type} Services</h2>
                            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                                {services.map((service) => (
                                    <Card key={service.id} className="hover:shadow-md transition-shadow">
                                        <div className="flex flex-col h-full">
                                            <div className="flex justify-between items-start">
                                                <h3 className="text-lg font-medium text-gray-900">{service.name}</h3>
                                                {service.isPopular && (
                                                    <Badge variant="warning">Popular</Badge>
                                                )}
                                            </div>
                                            <p className="mt-2 text-2xl font-bold text-gray-900">
                                                ${service.price.toFixed(2)}
                                            </p>
                                            <p className="mt-2 text-sm text-gray-500 flex-grow">
                                                {service.description}
                                            </p>
                                            <div className="mt-4 flex justify-end space-x-2">
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    className="flex items-center"
                                                    onClick={() => setEditingService(service)}
                                                >
                                                    <Edit size={14} className="mr-1" />
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    className="flex items-center"
                                                    onClick={() => handleDeleteService(service.id)}
                                                >
                                                    <Trash2 size={14} className="mr-1" />
                                                    Delete
                                                </Button>
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <Card>
                        <div className="text-center py-6">
                            <p className="text-gray-500">No services found.</p>
                        </div>
                    </Card>
                )}
            </div>

            {/* Add Service Modal */}
            {showAddModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <h3 className="text-lg font-medium text-gray-900">Add New Service</h3>
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                            Service Name
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            value={newService.name}
                                            onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                                            Price ($)
                                        </label>
                                        <input
                                            type="number"
                                            id="price"
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            value={newService.price}
                                            onChange={(e) => setNewService({ ...newService, price: parseFloat(e.target.value) })}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                                            Service Type
                                        </label>
                                        <select
                                            id="type"
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            value={newService.type}
                                            onChange={(e) => setNewService({ ...newService, type: e.target.value as any })}
                                        >
                                            <option value="Installation">Installation</option>
                                            <option value="Customization">Customization</option>
                                            <option value="Support">Support</option>
                                            <option value="Maintenance">Maintenance</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                            Description
                                        </label>
                                        <textarea
                                            id="description"
                                            rows={3}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            value={newService.description}
                                            onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <input
                                            id="isPopular"
                                            type="checkbox"
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            checked={newService.isPopular}
                                            onChange={(e) => setNewService({ ...newService, isPopular: e.target.checked })}
                                        />
                                        <label htmlFor="isPopular" className="ml-2 block text-sm text-gray-900">
                                            Mark as Popular
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <Button
                                    variant="primary"
                                    className="w-full sm:w-auto sm:ml-3"
                                    onClick={handleAddService}
                                    disabled={!newService.name || !newService.description || newService.price <= 0}
                                >
                                    Add Service
                                </Button>
                                <Button
                                    variant="secondary"
                                    className="mt-3 w-full sm:mt-0 sm:w-auto"
                                    onClick={() => setShowAddModal(false)}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Service Modal */}
            {editingService && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <h3 className="text-lg font-medium text-gray-900">Edit Service</h3>
                                <div className="mt-4 space-y-4">
                                    <div>
                                        <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                                            Service Name
                                        </label>
                                        <input
                                            type="text"
                                            id="edit-name"
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            value={editingService.name}
                                            onChange={(e) => setEditingService({ ...editingService, name: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="edit-price" className="block text-sm font-medium text-gray-700">
                                            Price ($)
                                        </label>
                                        <input
                                            type="number"
                                            id="edit-price"
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            value={editingService.price}
                                            onChange={(e) => setEditingService({ ...editingService, price: parseFloat(e.target.value) })}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="edit-type" className="block text-sm font-medium text-gray-700">
                                            Service Type
                                        </label>
                                        <select
                                            id="edit-type"
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            value={editingService.type}
                                            onChange={(e) => setEditingService({ ...editingService, type: e.target.value as any })}
                                        >
                                            <option value="Installation">Installation</option>
                                            <option value="Customization">Customization</option>
                                            <option value="Support">Support</option>
                                            <option value="Maintenance">Maintenance</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700">
                                            Description
                                        </label>
                                        <textarea
                                            id="edit-description"
                                            rows={3}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            value={editingService.description}
                                            onChange={(e) => setEditingService({ ...editingService, description: e.target.value })}
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <input
                                            id="edit-isPopular"
                                            type="checkbox"
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            checked={editingService.isPopular}
                                            onChange={(e) => setEditingService({ ...editingService, isPopular: e.target.checked })}
                                        />
                                        <label htmlFor="edit-isPopular" className="ml-2 block text-sm text-gray-900">
                                            Mark as Popular
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <Button
                                    variant="primary"
                                    className="w-full sm:w-auto sm:ml-3"
                                    onClick={handleUpdateService}
                                    disabled={!editingService.name || !editingService.description || editingService.price <= 0}
                                >
                                    Update Service
                                </Button>
                                <Button
                                    variant="secondary"
                                    className="mt-3 w-full sm:mt-0 sm:w-auto"
                                    onClick={() => setEditingService(null)}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AvailableServices;